import { memo, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import {
  Controller,
  useFormState,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormSetValue,
  type UseFormTrigger,
} from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import MessageIcon from "@mui/icons-material/Message";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import CodeIcon from "@mui/icons-material/Code";
import { MessageTemplateFormValues } from "../formTypes";
import { RICH_TEXT_ACTIONS, SUGGESTED_VARIABLES } from "../constants";
import { resolveMetaDefaultTemplateFixedVariables } from "../utils/fluxmedDefaultTemplateVariables";
import MessageTemplateVariablesPicker from "./MessageTemplateVariablesPicker";
import { useMessageTemplateFormDialogState } from "../hooks/useMessageTemplateFormDialogState";
import MessageTemplatePhonePreview from "./MessageTemplatePhonePreview";
import {
  HEADER_MAX_LENGTH,
  getAcceptedMimeTypesForHeaderMedia,
  normalizeTemplateName,
  normalizeVariableTokens,
  validateHeaderMediaFile,
  validateHeaderText,
  validateBodyText,
  validateButtonLabel,
  validateButtonUrl,
  validateTemplateName,
} from "../validation/messageTemplateValidation";
import {
  buildQuestionnaireTemplateButtonUrl,
  tryParseQuestionnaireIdFromButtonUrl,
} from "../utils/questionnaireTemplateButtonUrl";
import { useSessionContext } from "@/contexts/SessionContext";
import { currentFeatures, oids } from "@/configs/Settings";
import MessageDefaultTemplateUsageShortLine from "./MessageDefaultTemplateUsageShortLine";

export type MessageTemplateQuestionnaireOption = {
  id: string;
  title: string;
};

const QUESTIONNAIRE_TEMPLATE_BUTTON_LABEL = "Responder";
const RICH_TEXT_ACTION_ICONS = {
  bold: <FormatBoldIcon fontSize="small" />,
  italic: <FormatItalicIcon fontSize="small" />,
  strikethrough: <StrikethroughSIcon fontSize="small" />,
  monospace: <CodeIcon fontSize="small" />,
} as const;

function formValuesMediaLabel(
  file: File | null | undefined,
  fileName: string | undefined,
  hasExistingPreview: boolean,
): string {
  if (file?.name) return file.name;
  if (fileName?.trim()) return fileName.trim();
  if (hasExistingPreview) return "Amostra já vinculada ao modelo";
  return "";
}

export interface MessageTemplateFormDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId?: string;
  control: Control<MessageTemplateFormValues>;
  formErrors: FieldErrors<MessageTemplateFormValues>;
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  isSubmitting: boolean;
  hasSubmitError: boolean;
  submitErrorMessage?: string | null;
  resetForm: () => void;
  dialogTitle: string;
  submitLabel: string;
  existingTemplateNames?: string[];
  currentTemplateName?: string;
  triggerBodyValidation: () => void;
  triggerHeaderValidation: () => void;
  setValue: UseFormSetValue<MessageTemplateFormValues>;
  trigger: UseFormTrigger<MessageTemplateFormValues>;
  questionnaires: MessageTemplateQuestionnaireOption[];
  isQuestionnairesLoading?: boolean;
  questionnairesErrorMessage?: string | null;
  defaultTemplateUsageMessage?: string | null;
}

const MessageTemplateFormDialog = memo(
  ({
    open,
    onClose,
    organizationId,
    control,
    formErrors,
    handleSubmit,
    isSubmitting,
    hasSubmitError,
    submitErrorMessage = null,
    resetForm,
    dialogTitle,
    submitLabel,
    existingTemplateNames = [],
    currentTemplateName,
    triggerBodyValidation,
    triggerHeaderValidation,
    setValue,
    trigger,
    questionnaires,
    isQuestionnairesLoading = false,
    questionnairesErrorMessage = null,
    defaultTemplateUsageMessage = null,
  }: MessageTemplateFormDialogProps) => {
    const { access, user } = useSessionContext();
    const {
      activeVariableTarget,
      bodyFieldRef,
      bodyInputRef,
      buttonType,
      handleDialogClose,
      handleInsertVariable,
      hasButton,
      handleApplyRichFormat,
      headerFieldRef,
      headerInputRef,
      headerInsertLimitError,
      isEditing,
      previewButtonText,
      previewHeader,
      previewHeaderFormat,
      previewHeaderMediaFileName,
      previewHeaderMediaUrl,
      setActiveVariableTarget,
      setHeaderInsertLimitError,
      watchedBodyValue,
      isMediaHeader,
    } = useMessageTemplateFormDialogState({
      control,
      dialogTitle,
      isSubmitting,
      onClose,
      open,
      resetForm,
      triggerBodyValidation,
      triggerHeaderValidation,
    });

    const questionnaireLinkNotifierContext = useMemo(() => {
      const username = user?.username;
      if (
        access?.type !== "professional" ||
        !access.organizationId ||
        typeof username !== "string" ||
        !organizationId
      ) {
        return undefined;
      }
      return {
        practitionerRoleReference: `PractitionerRole/${access.organizationId}-${oids.cpf}-${username}`,
        ownerOrganizationReference: `Organization/${oids.cnpj}-${organizationId}`,
      };
    }, [access?.organizationId, access?.type, organizationId, user?.username]);

    const watchedButtonValue = useWatch({
      control,
      name: "buttonValue",
      defaultValue: "",
    });
    const suggestedVariables = useMemo(() => {
      if (defaultTemplateUsageMessage && currentTemplateName) {
        return (
          resolveMetaDefaultTemplateFixedVariables(
            currentTemplateName,
            organizationId,
          ) ?? []
        );
      }
      return [...SUGGESTED_VARIABLES];
    }, [
      currentTemplateName,
      defaultTemplateUsageMessage,
      organizationId,
    ]);

    const selectedQuestionnaireId = useMemo(() => {
      const parsed = tryParseQuestionnaireIdFromButtonUrl(watchedButtonValue);
      if (
        parsed &&
        questionnaires.some((questionnaire) => questionnaire.id === parsed)
      )
        return parsed;
      return "";
    }, [watchedButtonValue, questionnaires]);

    const activeVariableLabel = (() => {
      if (activeVariableTarget === "header") {
        return isMediaHeader ? "Corpo da mensagem" : "Cabeçalho";
      }
      if (
        hasButton &&
        (activeVariableTarget === "buttonText" ||
          activeVariableTarget === "buttonValue")
      ) {
        return "Corpo da mensagem";
      }
      if (activeVariableTarget === "buttonText") return "Texto do botão";
      if (activeVariableTarget === "buttonValue") return "URL do botão";
      return "Corpo da mensagem";
    })();

    const { isSubmitted } = useFormState({ control });
    const hasValidationErrors = Boolean(
      formErrors.name ||
      formErrors.header ||
      formErrors.headerFormat ||
      formErrors.headerMediaFile ||
      formErrors.body ||
      formErrors.buttonText ||
      formErrors.buttonValue,
    );
    const questionnaireFieldErrorMessage =
      formErrors.buttonValue?.message ?? formErrors.buttonText?.message;

    return (
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            mx: { xs: 1.5, sm: 0 },
            maxHeight: "calc(100% - 32px)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                backgroundColor: "rgba(25, 118, 210, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MessageIcon sx={{ color: "primary.main", fontSize: 28 }} />
            </Box>

            <Stack spacing={0.5} flex={1}>
              <Typography variant="h6" fontWeight={600}>
                {dialogTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isEditing
                  ? "Atualize os campos do template e salve as alterações."
                  : "Crie um novo template para mensagens clínicas automatizadas."}
              </Typography>
            </Stack>

            <IconButton
              aria-label="Fechar"
              onClick={handleDialogClose}
              disabled={isSubmitting}
              sx={{
                color: "text.secondary",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleSubmit} noValidate>
          <DialogContent
            sx={{
              pt: 3,
              pb: 2,
              overflow: "auto",
            }}
          >
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={3}
              alignItems={{ xs: "stretch", lg: "flex-start" }}
            >
              <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
                {defaultTemplateUsageMessage ? (
                  <MessageDefaultTemplateUsageShortLine
                    message={defaultTemplateUsageMessage}
                  />
                ) : null}
                {isSubmitted && hasValidationErrors ? (
                  <Alert severity="error" role="alert" variant="outlined">
                    Corrija os campos indicados abaixo antes de salvar o modelo.
                  </Alert>
                ) : null}
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: "Nome do modelo é obrigatório",
                    maxLength: {
                      value: 512,
                      message:
                        "Nome do modelo deve ter no máximo 512 caracteres",
                    },
                    validate: {
                      normalizedName: (value) => {
                        const validation = validateTemplateName(value);
                        return validation.valid || validation.error;
                      },
                      uniqueName: (value) => {
                        const normalizedValue = normalizeTemplateName(value);
                        const currentNormalized = normalizeTemplateName(
                          currentTemplateName ?? "",
                        );
                        const alreadyExists = existingTemplateNames.some(
                          (templateName) =>
                            normalizeTemplateName(templateName) ===
                            normalizedValue,
                        );

                        if (normalizedValue === currentNormalized) return true;
                        return (
                          !alreadyExists ||
                          "Já existe um template com esse nome para esta conta"
                        );
                      },
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Nome do modelo"
                      variant="filled"
                      error={Boolean(formErrors.name)}
                      helperText={
                        formErrors.name?.message ??
                        `Use até 512 caracteres. ${(field.value ?? "").length}/512 caracteres`
                      }
                      fullWidth
                      required
                      disabled={isSubmitting || isEditing}
                      inputProps={{ maxLength: 512 }}
                      onBlur={(event) => {
                        field.onChange(
                          normalizeTemplateName(event.target.value),
                        );
                        field.onBlur();
                      }}
                    />
                  )}
                />
                <Controller
                  name="headerFormat"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth variant="filled">
                      <InputLabel id="message-template-header-format-label">
                        Tipo de cabeçalho
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="message-template-header-format-label"
                        label="Tipo de cabeçalho"
                        disabled={isSubmitting}
                        onChange={(event) => {
                          const nextFormat = event.target
                            .value as MessageTemplateFormValues["headerFormat"];
                          const previousPreviewUrl = String(
                            previewHeaderMediaUrl ?? "",
                          );
                          if (
                            previousPreviewUrl.startsWith("blob:") &&
                            nextFormat !== "IMAGE" &&
                            nextFormat !== "VIDEO" &&
                            nextFormat !== "DOCUMENT"
                          ) {
                            URL.revokeObjectURL(previousPreviewUrl);
                          }
                          field.onChange(nextFormat);
                          if (nextFormat !== "TEXT") {
                            setValue("header", "");
                          }
                          if (
                            nextFormat !== "IMAGE" &&
                            nextFormat !== "VIDEO" &&
                            nextFormat !== "DOCUMENT"
                          ) {
                            setValue("headerMediaFile", null);
                            setValue("headerMediaHandle", "");
                            setValue("headerMediaPreviewUrl", "");
                            setValue("headerMediaFileName", "");
                          }
                          setHeaderInsertLimitError(null);
                          if (nextFormat !== "TEXT" && activeVariableTarget === "header") {
                            setActiveVariableTarget("body");
                          }
                        }}
                      >
                        <MenuItem value="NONE">Sem cabeçalho</MenuItem>
                        <MenuItem value="TEXT">Texto</MenuItem>
                        <MenuItem value="IMAGE">Foto (imagem)</MenuItem>
                        <MenuItem value="VIDEO">Vídeo</MenuItem>
                        <MenuItem value="DOCUMENT">Documento</MenuItem>
                      </Select>
                      <FormHelperText>
                        O cabeçalho aparece no topo da mensagem do WhatsApp.
                      </FormHelperText>
                    </FormControl>
                  )}
                />

                {previewHeaderFormat === "TEXT" ? (
                  <Controller
                    name="header"
                    control={control}
                    rules={{
                      validate: (value) => {
                        const normalizedHeader = normalizeVariableTokens(
                          value ?? "",
                        );
                        const result = validateHeaderText(normalizedHeader);
                        return result.valid || result.error;
                      },
                    }}
                    render={({ field }) => (
                      <Box>
                        <TextField
                          {...field}
                          placeholder="Digite o cabeçalho da mensagem (até 60 caracteres)"
                          label="Cabeçalho (texto)"
                          variant="filled"
                          error={
                            Boolean(formErrors.header) ||
                            Boolean(headerInsertLimitError)
                          }
                          fullWidth
                          disabled={isSubmitting}
                          inputProps={{ maxLength: HEADER_MAX_LENGTH }}
                          helperText={
                            headerInsertLimitError ??
                            formErrors.header?.message ??
                            `Texto curto para introduzir a mensagem. ${(field.value ?? "").length}/${HEADER_MAX_LENGTH} caracteres`
                          }
                          onBlur={(event) => {
                            field.onChange(
                              normalizeVariableTokens(event.target.value),
                            );
                            field.onBlur();
                            setHeaderInsertLimitError(null);
                          }}
                          onFocus={() => setActiveVariableTarget("header")}
                          inputRef={headerInputRef}
                        />
                        {(() => {
                          headerFieldRef.current = {
                            value: field.value ?? "",
                            onChange: field.onChange,
                          };
                          return null;
                        })()}
                      </Box>
                    )}
                  />
                ) : null}

                {previewHeaderFormat === "IMAGE" ||
                previewHeaderFormat === "VIDEO" ||
                previewHeaderFormat === "DOCUMENT" ? (
                  <Controller
                    name="headerMediaFile"
                    control={control}
                    rules={{
                      validate: (_value, formValues) => {
                        const format = formValues.headerFormat;
                        if (
                          format !== "IMAGE" &&
                          format !== "VIDEO" &&
                          format !== "DOCUMENT"
                        ) {
                          return true;
                        }
                        const result = validateHeaderMediaFile(
                          format,
                          formValues.headerMediaFile,
                          formValues.headerMediaHandle,
                        );
                        return result.valid || result.error;
                      },
                    }}
                    render={({ field }) => {
                      const accept = getAcceptedMimeTypesForHeaderMedia(
                        previewHeaderFormat,
                      );
                      const helperHint =
                        previewHeaderFormat === "IMAGE"
                          ? "JPG ou PNG, até 5 MB"
                          : previewHeaderFormat === "VIDEO"
                            ? "MP4, até 16 MB"
                            : "PDF, até 100 MB";
                      const selectedLabel =
                        formValuesMediaLabel(
                          field.value,
                          previewHeaderMediaFileName,
                          Boolean(String(previewHeaderMediaUrl ?? "").trim()),
                        );

                      return (
                        <Box>
                          <Button
                            component="label"
                            variant="outlined"
                            disabled={isSubmitting}
                            sx={{ textTransform: "none", mb: 1 }}
                          >
                            {selectedLabel
                              ? "Trocar arquivo de amostra"
                              : "Selecionar arquivo de amostra"}
                            <input
                              hidden
                              type="file"
                              accept={accept}
                              onChange={(event) => {
                                const file = event.target.files?.[0] ?? null;
                                const previousUrl = String(
                                  previewHeaderMediaUrl ?? "",
                                );
                                if (previousUrl.startsWith("blob:")) {
                                  URL.revokeObjectURL(previousUrl);
                                }
                                field.onChange(file);
                                if (file) {
                                  const objectUrl = URL.createObjectURL(file);
                                  setValue("headerMediaPreviewUrl", objectUrl);
                                  setValue("headerMediaFileName", file.name);
                                  setValue("headerMediaHandle", "");
                                } else {
                                  setValue("headerMediaPreviewUrl", "");
                                  setValue("headerMediaFileName", "");
                                }
                                void trigger("headerMediaFile");
                                event.target.value = "";
                              }}
                            />
                          </Button>
                          <Typography
                            variant="body2"
                            color={
                              formErrors.headerMediaFile
                                ? "error"
                                : "text.secondary"
                            }
                          >
                            {formErrors.headerMediaFile?.message ??
                              (selectedLabel
                                ? `Arquivo: ${selectedLabel}`
                                : `Amostra obrigatória para aprovação da Meta (${helperHint}).`)}
                          </Typography>
                        </Box>
                      );
                    }}
                  />
                ) : null}
                <Controller
                  name="body"
                  control={control}
                  rules={{
                    required: "Conteúdo da mensagem é obrigatório",
                    validate: (value) => {
                      const normalizedBody = normalizeVariableTokens(
                        value ?? "",
                      );
                      const validation = validateBodyText(normalizedBody);
                      return validation.valid || validation.error;
                    },
                  }}
                  render={({ field }) => {
                    return (
                      <Box>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          {RICH_TEXT_ACTIONS.map((action) => (
                            <Tooltip
                              key={`body-${action.key}`}
                              title={`${action.label} (${action.hint})`}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={isSubmitting}
                                  onClick={() =>
                                    handleApplyRichFormat(action.key, "body")
                                  }
                                  aria-label={`Aplicar ${action.label.toLowerCase()} no conteúdo`}
                                >
                                  {RICH_TEXT_ACTION_ICONS[action.key]}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ))}
                        </Stack>
                        <TextField
                          {...field}
                          placeholder="Digite o texto principal da mensagem e use variáveis para personalizar."
                          label="Conteúdo da mensagem"
                          variant="filled"
                          multiline
                          rows={5}
                          error={Boolean(formErrors.body)}
                          helperText={
                            formErrors.body?.message ??
                            `Use variáveis para personalizar a mensagem. ${(field.value ?? "").length}/1024 caracteres`
                          }
                          fullWidth
                          disabled={isSubmitting}
                          inputRef={bodyInputRef}
                          inputProps={{ maxLength: 1024 }}
                          onBlur={(event) => {
                            field.onChange(
                              normalizeVariableTokens(event.target.value),
                            );
                            field.onBlur();
                            triggerBodyValidation();
                          }}
                          onFocus={() => setActiveVariableTarget("body")}
                          sx={{
                            "& .MuiFilledInput-input": {
                              fontFamily: "monospace",
                              fontSize: "0.875rem",
                            },
                          }}
                        />
                        {(() => {
                          bodyFieldRef.current = {
                            value: field.value ?? "",
                            onChange: field.onChange,
                          };
                          return null;
                        })()}
                      </Box>
                    );
                  }}
                />

                {currentFeatures.defaultApp ? (
                  <>
                    <Controller
                      name="hasButton"
                      control={control}
                      render={({ field }) => (
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            p: 2,
                            backgroundColor: "background.default",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(field.value)}
                                onChange={(_, checked) => {
                                  field.onChange(checked);
                                  if (checked) {
                                    setValue("buttonType", "URL");
                                  } else {
                                    setValue("buttonText", "");
                                    setValue("buttonValue", "");
                                    setValue("buttonType", "URL");
                                    void trigger(["buttonText", "buttonValue"]);
                                  }
                                }}
                                disabled={isSubmitting}
                              />
                            }
                            label="Adicionar questionário ao template "
                          />
                          <Typography variant="caption" color="text.secondary">
                            Adiciona um botão (URL) que abre o questionário
                            selecionado.
                          </Typography>
                          {hasButton && (
                            <>
                              <Controller
                                name="buttonText"
                                control={control}
                                rules={{
                                  validate: (value) => {
                                    if (!hasButton) return true;
                                    const result = validateButtonLabel(
                                      value ?? "",
                                    );
                                    return result.valid || result.error;
                                  },
                                }}
                                render={() => (
                                  <span
                                    style={{ display: "none" }}
                                    aria-hidden
                                  />
                                )}
                              />
                              <Controller
                                name="buttonValue"
                                control={control}
                                rules={{
                                  validate: (value) => {
                                    if (!hasButton) return true;
                                    const normalizedValue =
                                      normalizeVariableTokens(value ?? "");
                                    const result =
                                      validateButtonUrl(normalizedValue);
                                    return result.valid || result.error;
                                  },
                                }}
                                render={() => (
                                  <span
                                    style={{ display: "none" }}
                                    aria-hidden
                                  />
                                )}
                              />
                              <Stack
                                spacing={2}
                                sx={{
                                  mt: 4,
                                }}
                              >
                                <Stack spacing={0.5}>
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight={600}
                                  >
                                    Questionário do botão
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Escolha o questionário. O botão será exibido
                                    como &quot;Responder&quot; e a URL é montada
                                    automaticamente (tipo URL).
                                  </Typography>
                                </Stack>
                                {questionnairesErrorMessage ? (
                                  <Alert severity="warning">
                                    {questionnairesErrorMessage}
                                  </Alert>
                                ) : null}
                                {isQuestionnairesLoading ? (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1.5}
                                  >
                                    <CircularProgress size={18} />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Carregando questionários…
                                    </Typography>
                                  </Stack>
                                ) : null}
                                <FormControl
                                  fullWidth
                                  variant="filled"
                                  disabled={
                                    isSubmitting || isQuestionnairesLoading
                                  }
                                  error={Boolean(
                                    questionnaireFieldErrorMessage,
                                  )}
                                >
                                  <InputLabel id="template-questionnaire-label">
                                    Questionário
                                  </InputLabel>
                                  <Select
                                    labelId="template-questionnaire-label"
                                    label="Questionário"
                                    value={selectedQuestionnaireId}
                                    onChange={(event) => {
                                      const id = String(
                                        event.target.value ?? "",
                                      );
                                      if (!id) {
                                        setValue("buttonText", "");
                                        setValue("buttonValue", "");
                                        void trigger([
                                          "buttonText",
                                          "buttonValue",
                                        ]);
                                        return;
                                      }
                                      setValue("buttonType", "URL");
                                      setValue(
                                        "buttonText",
                                        QUESTIONNAIRE_TEMPLATE_BUTTON_LABEL,
                                      );
                                      setValue(
                                        "buttonValue",
                                        buildQuestionnaireTemplateButtonUrl(
                                          id,
                                          {
                                            notifierContext:
                                              questionnaireLinkNotifierContext,
                                          },
                                        ),
                                      );
                                      void trigger([
                                        "buttonText",
                                        "buttonValue",
                                      ]);
                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>Selecione um questionário</em>
                                    </MenuItem>
                                    {questionnaires.map((q) => (
                                      <MenuItem key={q.id} value={q.id}>
                                        {q.title}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {questionnaireFieldErrorMessage ? (
                                    <FormHelperText error>
                                      {questionnaireFieldErrorMessage}
                                    </FormHelperText>
                                  ) : null}
                                  {!isQuestionnairesLoading &&
                                    !questionnairesErrorMessage &&
                                    questionnaires.length === 0 ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ mt: 1 }}
                                    >
                                      Nenhum questionário encontrado para esta
                                      organização. Cadastre um na aba
                                      Questionários.
                                    </Typography>
                                  ) : null}
                                </FormControl>
                              </Stack>
                            </>
                          )}
                        </Box>
                      )}
                    />
                  </>
                ) : null}

                <MessageTemplateVariablesPicker
                  variableNames={suggestedVariables}
                  activeFieldLabel={activeVariableLabel}
                  onInsertVariable={handleInsertVariable}
                  isDefaultTemplate={Boolean(defaultTemplateUsageMessage)}
                />
                {hasSubmitError && (
                  <Alert severity="error">
                    {submitErrorMessage ??
                      "Erro ao salvar template. Tente novamente."}
                  </Alert>
                )}
              </Stack>
              <Box
                sx={{
                  flexShrink: 0,
                  width: { xs: "100%", lg: "auto" },
                  position: { lg: "sticky" },
                  top: { lg: 0 },
                  alignSelf: { xs: "center", lg: "flex-start" },
                  pt: { xs: 1, lg: 0 },
                }}
              >
                <MessageTemplatePhonePreview
                  header={String(previewHeader ?? "")}
                  headerFormat={previewHeaderFormat}
                  headerMediaUrl={String(previewHeaderMediaUrl ?? "")}
                  headerMediaFileName={String(previewHeaderMediaFileName ?? "")}
                  body={String(watchedBodyValue ?? "")}
                  hasButton={Boolean(hasButton)}
                  buttonText={String(previewButtonText ?? "")}
                  buttonType={buttonType}
                />
              </Box>
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Stack
              direction={{ xs: "column-reverse", sm: "row" }}
              spacing={1.5}
              width="100%"
              justifyContent="flex-end"
            >
              <Button
                onClick={handleDialogClose}
                disabled={isSubmitting}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  minHeight: 44,
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  textTransform: "none",
                  minHeight: 44,
                }}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
              >
                {isSubmitting ? "Salvando..." : submitLabel}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
    );
  },
);

MessageTemplateFormDialog.displayName = "MessageTemplateFormDialog";

export default MessageTemplateFormDialog;
